/* Created by Interactive 63 - https://interactive63.com */
/**
 * Simplifies an imported SVG so each separately drawable part is its own element: compound paths
 * are split into one path per shape, groups that only wrap a single element are removed, and
 * static transforms are folded into path data. Each step is skipped wherever it could change how
 * the artwork looks, and nothing inside defs, clip paths, masks or patterns is touched.
 */
import svgpath from 'svgpath';
import { IDENTITY, parseTransform, type Matrix } from '../geometry/matrix';
import { GEOMETRY_TAGS } from '../model/types';
import { splitPathData } from './split';

const SVG_NS = 'http://www.w3.org/2000/svg';

/** Splitting stops once the document would pass this many elements, which the editor handles well. */
export const MAX_SIMPLIFIED_ELEMENTS = 2000;
/** Decimal places kept in path data after a transform is folded in. */
const BAKE_DECIMALS = 4;
/** Tolerance when deciding whether a transform scales both axes equally. */
const SIMILARITY_TOLERANCE = 1e-6;

/** Containers whose contents are referenced by other elements and must be left alone. */
const REFERENCED_CONTAINERS = new Set([
  'defs',
  'clippath',
  'mask',
  'pattern',
  'symbol',
  'marker',
  'filter',
  'lineargradient',
  'radialgradient',
  'style',
  'title',
  'desc',
  'metadata',
]);

/** Paint and stroke settings a group passes to its content, which can move onto a lone child. */
const PASSED_DOWN = [
  'fill',
  'fill-rule',
  'clip-rule',
  'stroke',
  'stroke-width',
  'stroke-linecap',
  'stroke-linejoin',
  'stroke-miterlimit',
  'stroke-dasharray',
  'color',
  'font-family',
  'font-size',
  'font-weight',
];

/** Attributes that act on a whole element, so a split path hands them to its wrapper group. */
const WHOLE_ELEMENT_ATTRIBUTES = [
  'id',
  'data-st-label',
  'opacity',
  'clip-path',
  'mask',
  'filter',
  'transform',
];

/** Attributes on a group that do not stop it being dissolved into its only child. */
const DISSOLVABLE_ATTRIBUTES = new Set([...PASSED_DOWN, 'opacity', 'transform']);

/** Tags a dissolved group's child may have. */
const DISSOLVE_CHILD_TAGS = new Set([...GEOMETRY_TAGS, 'g', 'text', 'image', 'use']);

/** Attributes that tie a path to a coordinate system a baked transform would change. */
const POSITION_BOUND_ATTRIBUTES = [
  'clip-path',
  'mask',
  'filter',
  'marker',
  'marker-start',
  'marker-mid',
  'marker-end',
];

/** Whether an element sits inside a container that other elements refer to. */
function inReferencedContainer(el: Element): boolean {
  for (let p = el.parentElement; p; p = p.parentElement) {
    if (REFERENCED_CONTAINERS.has(p.localName.toLowerCase())) return true;
  }
  return false;
}

/** The value of a presentation attribute for an element: its own, else the nearest ancestor's. */
function resolved(el: Element, name: string): string | undefined {
  for (let p: Element | null = el; p; p = p.parentElement) {
    const value = p.getAttribute(name);
    if (value !== null) return value.trim();
  }
  return undefined;
}

/** The element a `url(#id)` paint value points at. */
function paintTarget(doc: Document, value: string | undefined): Element | null {
  const id = /url\(\s*['"]?#([^'")\s]+)/.exec(value ?? '')?.[1];
  return id ? doc.querySelector(`[id="${id}"]`) : null;
}

/** Moves the listed attributes from one element to another. */
function moveAttributes(from: Element, to: Element, names: readonly string[]): void {
  for (const name of names) {
    const value = from.getAttribute(name);
    if (value === null) continue;
    to.setAttribute(name, value);
    from.removeAttribute(name);
  }
}

export interface SplitOutcome {
  /** The group that took over the path's identity, when one was needed or asked for. */
  wrapper: Element | null;
  parts: Element[];
}

/**
 * Replaces a compound path with one path per separate shape. Attributes that act on the whole
 * element (id, label, opacity, clip, mask, filter, transform) go to a wrapper group, which is made
 * when the path has any of them or when `forceWrapper` is set. Returns null when the path is a
 * single shape or cannot be read, and 'too-many' when more than `maxAdded` elements would be
 * needed.
 */
export function splitPathElement(
  path: Element,
  forceWrapper: boolean,
  maxAdded = Number.POSITIVE_INFINITY,
): SplitOutcome | 'too-many' | null {
  const d = path.getAttribute('d');
  if (!d) return null;
  const openAsClosed = resolved(path, 'fill') !== 'none';
  const shapes = splitPathData(d, { openAsClosed });
  if (shapes.length < 2) return null;
  const needsWrapper =
    forceWrapper || WHOLE_ELEMENT_ATTRIBUTES.some((name) => path.hasAttribute(name));
  if (shapes.length - 1 + (needsWrapper ? 1 : 0) > maxAdded) return 'too-many';

  const wrapper = needsWrapper ? path.ownerDocument.createElementNS(SVG_NS, 'g') : null;
  const parts = shapes.map((shape) => {
    const part = path.cloneNode(false) as Element;
    part.setAttribute('d', shape);
    if (wrapper) {
      for (const name of WHOLE_ELEMENT_ATTRIBUTES) part.removeAttribute(name);
    } else {
      part.removeAttribute('id');
      part.removeAttribute('data-st-label');
    }
    return part;
  });
  if (wrapper) {
    moveAttributes(path, wrapper, WHOLE_ELEMENT_ATTRIBUTES);
    wrapper.append(...parts);
    path.replaceWith(wrapper);
  } else {
    path.replaceWith(...parts);
  }
  return { wrapper, parts };
}

/** Splits every compound path outside referenced containers. Returns whether any was left whole. */
function splitCompoundPaths(doc: Document): boolean {
  let count = doc.getElementsByTagName('*').length;
  let capped = false;
  for (const path of Array.from(doc.getElementsByTagName('path'))) {
    if (inReferencedContainer(path)) continue;
    const before = doc.getElementsByTagName('*').length;
    const outcome = splitPathElement(path, false, MAX_SIMPLIFIED_ELEMENTS - count);
    if (outcome === 'too-many') capped = true;
    else if (outcome) count += doc.getElementsByTagName('*').length - before;
  }
  return capped;
}

/** Multiplies two opacity values written as attributes. */
function combinedOpacity(a: string, b: string): string {
  const product = Number(a) * Number(b);
  return String(Math.round(product * 1e4) / 1e4);
}

/** Whether a group can be dissolved without changing the picture, and the one child if so. */
function dissolvableChild(group: Element): Element | null {
  if (group.children.length !== 1) return null;
  const onlyText = Array.from(group.childNodes).every(
    (node) => node.nodeType !== 3 || (node.textContent ?? '').trim() === '',
  );
  if (!onlyText) return null;
  for (const attribute of Array.from(group.attributes)) {
    if (!DISSOLVABLE_ATTRIBUTES.has(attribute.name)) return null;
  }
  const child = group.children[0];
  return child && DISSOLVE_CHILD_TAGS.has(child.localName.toLowerCase()) ? child : null;
}

/** Removes groups that wrap a single element, handing their settings to it. */
function dissolveSingleChildGroups(doc: Document): void {
  const groups = Array.from(doc.getElementsByTagName('g')).reverse();
  for (const group of groups) {
    if (group.parentElement === null || inReferencedContainer(group)) continue;
    const child = dissolvableChild(group);
    if (!child) continue;

    for (const name of PASSED_DOWN) {
      const value = group.getAttribute(name);
      if (value !== null && !child.hasAttribute(name)) child.setAttribute(name, value);
    }
    const opacity = group.getAttribute('opacity');
    if (opacity !== null) {
      const own = child.getAttribute('opacity');
      child.setAttribute('opacity', own === null ? opacity : combinedOpacity(opacity, own));
    }
    const transform = group.getAttribute('transform');
    if (transform !== null) {
      child.setAttribute(
        'transform',
        `${transform} ${child.getAttribute('transform') ?? ''}`.trim(),
      );
    }
    group.replaceWith(child);
  }
}

/** The uniform scale of a matrix that only turns, flips and scales evenly, or null. */
function similarityScale(m: Matrix): number | null {
  const [a, b, c, d] = m;
  const columnA = a * a + b * b;
  const columnB = c * c + d * d;
  const scale = Math.sqrt(columnA);
  const scaleTolerance = SIMILARITY_TOLERANCE * Math.max(1, columnA);
  if (Math.abs(columnA - columnB) > scaleTolerance) return null;
  if (Math.abs(a * c + b * d) > scaleTolerance) return null;
  return scale;
}

/** Reads a stroke width given as a plain number or in px. */
function plainLength(value: string | undefined): number | null {
  if (value === undefined) return 1;
  const match = /^([+-]?\d*\.?\d+(?:e[+-]?\d+)?)(px)?$/i.exec(value);
  return match ? Number(match[1]) : null;
}

/**
 * Why a path's own transform cannot be folded into its data without changing how it looks, or
 * null when it can. A clip, mask, filter or marker, a gradient or pattern that depends on the
 * coordinate system, or a stroke that would change shape all stop it.
 */
export function bakeBlocker(el: Element): string | null {
  if (el.localName.toLowerCase() !== 'path' || !el.getAttribute('d')) {
    return 'Only paths can have their transform flattened.';
  }
  const matrix = parseTransform(el.getAttribute('transform'));
  if (!matrix) return "This layer's transform could not be read.";
  if (POSITION_BOUND_ATTRIBUTES.some((name) => el.hasAttribute(name))) {
    return 'It has a clip, mask, filter or marker that depends on its transform.';
  }

  const [a, b, c, d] = matrix;
  const axisAligned = b === 0 && c === 0 && a > 0 && d > 0;
  for (const name of ['fill', 'stroke']) {
    const value = resolved(el, name);
    if (!value?.startsWith('url(')) continue;
    const target = paintTarget(el.ownerDocument, value);
    if (target?.localName.toLowerCase() === 'pattern') {
      return 'A pattern fill or stroke depends on its transform.';
    }
    const units = target?.getAttribute('gradientUnits');
    if (units === 'userSpaceOnUse') return 'A gradient on it depends on its transform.';
    if (!axisAligned)
      return 'A gradient on it would turn differently if the transform were flattened.';
  }

  const stroke = resolved(el, 'stroke');
  if (stroke === undefined || stroke === 'none') return null;
  if (resolved(el, 'vector-effect') === 'non-scaling-stroke') {
    return 'Its stroke keeps a fixed width, which depends on its transform.';
  }
  const scale = similarityScale(matrix);
  if (scale === null)
    return 'Its stroke would change shape, because the transform scales unevenly.';
  if (Math.abs(scale - 1) < SIMILARITY_TOLERANCE) return null;
  const dashes = resolved(el, 'stroke-dasharray');
  if (dashes !== undefined && dashes !== 'none') return 'Its dashed stroke would need rescaling.';
  if (plainLength(resolved(el, 'stroke-width')) === null) {
    return 'Its stroke width is not a plain number.';
  }
  return null;
}

/**
 * Folds a path's own `transform` into its data, scaling a stroke width to match. Call
 * `bakeBlocker` first; returns false and changes nothing when the path cannot be flattened.
 */
export function bakeTransform(el: Element): boolean {
  if (bakeBlocker(el) !== null) return false;
  const matrix = parseTransform(el.getAttribute('transform')) ?? IDENTITY;
  const d = el.getAttribute('d') ?? '';
  const stroke = resolved(el, 'stroke');
  const scale = similarityScale(matrix) ?? 1;
  if (stroke !== undefined && stroke !== 'none' && Math.abs(scale - 1) >= SIMILARITY_TOLERANCE) {
    const width = plainLength(resolved(el, 'stroke-width')) ?? 1;
    el.setAttribute('stroke-width', String(Math.round(width * scale * 1e4) / 1e4));
  }
  el.setAttribute(
    'd',
    svgpath(d)
      .matrix([...matrix])
      .round(BAKE_DECIMALS)
      .toString(),
  );
  el.removeAttribute('transform');
  return true;
}

/** Folds the static transform of every path outside referenced containers into its data. */
function bakePathTransforms(doc: Document): void {
  for (const path of Array.from(doc.getElementsByTagName('path'))) {
    if (!path.hasAttribute('transform') || inReferencedContainer(path)) continue;
    bakeTransform(path);
  }
}

/**
 * Simplifies a document in place and returns warnings for the import. Splitting runs first, then
 * groups that wrap one element are removed, then transforms are folded into the paths.
 */
export function simplifyDocument(doc: Document): string[] {
  const warnings: string[] = [];
  if (splitCompoundPaths(doc)) {
    warnings.push(
      `Some paths were left whole because splitting them would pass ${MAX_SIMPLIFIED_ELEMENTS} layers.`,
    );
  }
  dissolveSingleChildGroups(doc);
  bakePathTransforms(doc);
  return warnings;
}
